/**
 * AI CONTEXT
 * Purpose: student task board with filters (all/urgent/done) and add-task action.
 * Inputs: AdminViewContext.studentTasks.
 * Uses: StudentTaskCard list rendering.
 * Used by: SuperView/AdminView side panels.
 * Refactor: persist selected task filter per user settings.
 */
import { useMemo, useState } from "react"

import ComponentErrorBoundary from "#blocks/ComponentErrorBoundary"
import EmptyState from "#blocks/EmptyState"
import Flex from "#blocks/Flex"
import List from "#blocks/List"
import RadioGroup from "#blocks/RadioGroup"
import { useAdminViewContext } from "#contexts/AdminViewContextProvider"
import { StudentTaskCardData } from "#customTypes/composedTypes/studentTaskTypes"
import { sortTasks } from "#utils/taskHelpers"
import useIsSuperView from "#views/SuperView/useIsSuperView"

import AddStudentTaskForm from "./AddStudentTaskForm"
import StudentTaskCard from "./StudentTaskCard"

const StudentTaskCardWithBoundary = ({
  item,
}: {
  item: StudentTaskCardData
}) => (
  <ComponentErrorBoundary
    context={`Task: ${item.header}`}
    fallbackStyle={{ width: 370, minHeight: 60 }}
  >
    <StudentTaskCard item={item} />
  </ComponentErrorBoundary>
)

enum VIEWS {
  ALL = "ALL",
  URGENT = "URGENT",
  DONE = "DONE",
}

const PADDING_RIGHT = 30

const StudentTaskList = () => {
  const { studentTasks, presentHighlights } = useAdminViewContext()
  const [view, setView] = useState(VIEWS.ALL)

  const isSuperView = useIsSuperView()

  const filteredTasks = useMemo(() => {
    // Students just marked present float to the top of the pile.
    const sortedTasks = sortTasks(studentTasks, presentHighlights)
    if (view === VIEWS.ALL) return sortedTasks
    if (view === VIEWS.URGENT)
      return sortedTasks.filter((task) => task.isUrgent)
    if (view === VIEWS.DONE)
      return sortedTasks.filter((task) => task.isAllTasksCompleted)
    return sortedTasks
  }, [studentTasks, presentHighlights, view])

  return (
    <Flex
      style={{
        padding: isSuperView ? "12px 0px 12px 14px" : "30px 0px 0px 30px",
        border: "1px solid gray",
        borderRadius: 9,
        width: "100%",
        height: "100%",
        flexGrow: 1,
        position: "relative",
        overflow: "hidden",
      }}
      col
      gap={10}
    >
      <Flex
        gap={isSuperView ? 20 : 70}
        style={{
          paddingRight: isSuperView ? 10 : PADDING_RIGHT,
          alignItems: "center",
          justifyContent: isSuperView ? "space-between" : "flex-start",
        }}
      >
        {isSuperView ? <h2>Tasks</h2> : <h1>Tasks</h1>}

        <RadioGroup
          data-testid="student-task-filter"
          options={[
            { label: "All", value: VIEWS.ALL },
            { label: "Urgent", value: VIEWS.URGENT },
            { label: "Done", value: VIEWS.DONE },
          ]}
          onChange={(e) => setView(e.target.value)}
          value={view}
          style={{
            fontSize: isSuperView ? "10px" : undefined,
          }}
          size={isSuperView ? "small" : undefined}
        />
        {/* display: contents keeps the wrapper out of the header's flex layout;
            it exists because this icon-only "plus" button is otherwise
            indistinguishable by role/name from ExceptionList's add button, and
            the button itself is rendered inside ButtonToggleModal. */}
        <span data-testid="add-student-task" style={{ display: "contents" }}>
          <AddStudentTaskForm studentId={null} showLabel={!isSuperView} />
        </span>
      </Flex>

      <Flex
        style={{
          flexGrow: 1,
          height: isSuperView ? "calc(100% - 30px)" : "100%",
          paddingRight: isSuperView ? 4 : PADDING_RIGHT,
        }}
      >
        {filteredTasks.length ? (
          <List
            layout={isSuperView ? "vertical" : "grid"}
            items={filteredTasks}
            component={StudentTaskCardWithBoundary}
            gap={isSuperView ? 10 : 30}
          />
        ) : (
          <EmptyState />
        )}
      </Flex>
    </Flex>
  )
}

export default StudentTaskList
